import {useEffect, useId, useRef} from "react";
import {css} from "@panda/css";

import {Loader} from "@pg-design/loader-module";
import {ResponsiveModal} from "@pg-design/modal-module";
import {OFFER_TEST_IDS} from "@pg-mono/test-ids";

import {getUnlockChoiceOptions} from "../../../constants/unlock_choice_options";
import {IUnlockChoiceModalLayoutProps} from "../../../types/IUnlockChoiceModalLayoutProps";
import {isUnlockChoiceOfferOrReservation} from "../../../utils/is_unlock_choice_offer_or_reservation";
import {UnlockChoiceModalFooter} from "./UnlockChoiceModalFooter";
import {UnlockChoiceModalOptionCard} from "./UnlockChoiceModalOptionCard";
import {UnlockChoiceModalPriceHeader} from "./UnlockChoiceModalPriceHeader";
import {UnlockChoiceModalPriceNegotiation} from "./UnlockChoiceModalPriceNegotiation";
import {UnlockChoiceModalPriceNegotiationFooter} from "./UnlockChoiceModalPriceNegotiationFooter";
import {UnlockChoiceModalTitle} from "./UnlockChoiceModalTitle";
import {UnlockChoiceSentContent} from "./UnlockChoiceSentContent";

export const UnlockChoiceModalLayout = (props: IUnlockChoiceModalLayoutProps) => {
    const {
        isOpen,
        isLoading,
        entryPoint,
        firstName,
        price,
        offerType,
        vendorName,
        privacyPolicyUrl,
        selectedOptionId,
        isSending,
        isConfirmDisabled,
        confirmation,
        isPriceNegotiationShown,
        isPriceNegotiationArmed,
        onSelectOption,
        onConfirm,
        onClose,
        onAfterClose,
        zIndex
    } = props;

    const titleId = useId();
    const descriptionId = useId();
    const optionInputName = useId();
    const radioGroupRef = useRef<HTMLDivElement>(null);
    const wasLoadingRef = useRef(isLoading);
    const isConfirmation = confirmation !== null;
    const isPriceNegotiation = !isConfirmation && isPriceNegotiationShown;
    const isChoice = !isConfirmation && !isPriceNegotiation;
    const isDescribed = isConfirmation || isPriceNegotiation;

    useEffect(() => {
        const wasLoading = wasLoadingRef.current;
        wasLoadingRef.current = isLoading;

        const radioGroup = radioGroupRef.current;
        const dialog = radioGroup?.closest('[role="dialog"]');
        const activeElement = document.activeElement;

        if (!wasLoading || isLoading || !radioGroup || !dialog || (activeElement !== dialog && dialog.contains(activeElement))) {
            return;
        }

        radioGroup.querySelector<HTMLInputElement>("input:checked")?.focus({preventScroll: true});
    }, [isLoading]);

    useEffect(() => {
        if (!isDescribed) {
            return;
        }

        const dialog = document.getElementById(titleId)?.closest<HTMLElement>('[role="dialog"]');

        if (dialog && !dialog.contains(document.activeElement)) {
            dialog.focus({preventScroll: true});
        }
    }, [isConfirmation, isPriceNegotiation]);

    const options = getUnlockChoiceOptions(entryPoint, offerType);
    const selectedOption = options.find((option) => option.id === selectedOptionId) ?? options[0];
    const headerPrice = !isLoading && isUnlockChoiceOfferOrReservation(entryPoint) ? price : null;
    const isLabelledByTitle = isDescribed || !isLoading;

    const getDataTestId = () => {
        if (confirmation) {
            return OFFER_TEST_IDS.LEAD_FORM.UNLOCK_CHOICE_CONFIRMATION;
        }

        if (isPriceNegotiation) {
            return OFFER_TEST_IDS.LEAD_FORM.UNLOCK_CHOICE_PRICE_NEGOTIATION;
        }

        return isLoading ? OFFER_TEST_IDS.LEAD_FORM.UNLOCK_CHOICE_LOADER : OFFER_TEST_IDS.LEAD_FORM.UNLOCK_CHOICE_MODAL;
    };

    const getHeaderClassName = () => {
        if (isLoading) {
            return loaderHeaderStyle;
        }

        return headerPrice ? priceHeaderStyle : titleHeaderStyle;
    };

    const getBodyClassName = () => {
        if (isDescribed) {
            return undefined;
        }

        if (isLoading) {
            return loaderBodyStyle;
        }

        return headerPrice ? priceBodyStyle : titleBodyStyle;
    };

    const renderHeaderContent = () => {
        if (isLoading) {
            return null;
        }

        if (headerPrice) {
            return <UnlockChoiceModalPriceHeader price={headerPrice} offerType={offerType} />;
        }

        return <UnlockChoiceModalTitle id={titleId} firstName={firstName} variant="header" className={headerTitleStyle} />;
    };

    const renderBodyContent = () => {
        if (confirmation) {
            return (
                <UnlockChoiceSentContent titleId={titleId} descriptionId={descriptionId} vendorName={confirmation.vendorName} subject={confirmation.subject} />
            );
        }

        if (isPriceNegotiation) {
            return <UnlockChoiceModalPriceNegotiation titleId={titleId} descriptionId={descriptionId} offerType={offerType} />;
        }

        if (isLoading) {
            return <Loader size="lg" fullWidth />;
        }

        return (
            <>
                {headerPrice && <UnlockChoiceModalTitle id={titleId} firstName={firstName} variant="question" />}

                <div ref={radioGroupRef} role="radiogroup" aria-labelledby={titleId} className={headerPrice ? priceOptionsStyle : titleOptionsStyle}>
                    {options.map((option) => (
                        <UnlockChoiceModalOptionCard
                            key={option.id}
                            option={option}
                            inputName={optionInputName}
                            isSelected={option.id === selectedOption.id}
                            isDisabled={isSending}
                            onSelect={onSelectOption}
                        />
                    ))}
                </div>
            </>
        );
    };

    return (
        <ResponsiveModal
            isOpen={isOpen}
            onClose={onClose}
            onAfterClose={onAfterClose}
            shouldCloseOnOverlayClick={isPriceNegotiation && isPriceNegotiationArmed}
            zIndex={zIndex}
            contentLabel={isLabelledByTitle ? undefined : "Wczytywanie opcji"}
            ariaLabelledBy={isLabelledByTitle ? titleId : undefined}
            ariaDescribedBy={isDescribed ? descriptionId : undefined}
            dataTestId={getDataTestId()}
        >
            {isChoice && (
                <ResponsiveModal.Header divider={headerPrice !== null} className={getHeaderClassName()}>
                    {renderHeaderContent()}
                    <ResponsiveModal.CloseButton
                        className={headerPrice ? priceHeaderCloseButtonStyle : undefined}
                        dataTestId={OFFER_TEST_IDS.LEAD_FORM.UNLOCK_CHOICE_CLOSE_BUTTON}
                    />
                </ResponsiveModal.Header>
            )}

            <ResponsiveModal.Body className={getBodyClassName()}>{renderBodyContent()}</ResponsiveModal.Body>

            {isPriceNegotiation && (
                <UnlockChoiceModalPriceNegotiationFooter
                    vendorName={vendorName}
                    privacyPolicyUrl={privacyPolicyUrl}
                    isSending={isSending}
                    isConfirmDisabled={isConfirmDisabled}
                    isArmed={isPriceNegotiationArmed}
                    onConfirm={onConfirm}
                />
            )}

            {isChoice && !isLoading && (
                <UnlockChoiceModalFooter
                    vendorName={vendorName}
                    privacyPolicyUrl={privacyPolicyUrl}
                    isClauseVisible={selectedOption.sendsLead}
                    ctaText={selectedOption.ctaText}
                    isSending={isSending}
                    isConfirmDisabled={isConfirmDisabled}
                    onConfirm={onConfirm}
                />
            )}
        </ResponsiveModal>
    );
};

const loaderHeaderStyle = css({
    display: "flex",
    justifyContent: "flex-end",
    px: 2,
    py: 1.5,
    md: {
        px: 3
    }
});

const priceHeaderStyle = css({
    position: "relative",
    pt: 1.5,
    pb: 3,
    pl: 2,
    pr: 6,
    md: {
        pb: 1.5,
        pl: 3,
        pr: 7
    }
});

const priceHeaderCloseButtonStyle = css({
    position: "absolute",
    top: 1.5,
    right: 2,
    md: {
        right: 3
    }
});

const titleHeaderStyle = css({
    display: "flex",
    alignItems: "flex-start",
    columnGap: "1rem",
    px: 2,
    py: 1.5,
    md: {
        px: 3
    }
});

const headerTitleStyle = css({
    flex: "1 1 auto",
    minWidth: 0
});

const priceBodyStyle = css({
    px: 2,
    pt: 2,
    pb: 1,
    md: {
        px: 3,
        pt: 3.5,
        pb: 2
    }
});

const titleBodyStyle = css({
    px: 2,
    pt: 1,
    pb: 1,
    md: {
        px: 3,
        pt: 3,
        pb: 2
    }
});

const priceOptionsStyle = css({
    display: "flex",
    flexDirection: "column",
    rowGap: 1.5,
    pt: 1.5,
    md: {
        rowGap: 2,
        pt: 3.5
    }
});

const titleOptionsStyle = css({
    display: "flex",
    flexDirection: "column",
    rowGap: 1.5,
    md: {
        rowGap: 2
    }
});

const loaderBodyStyle = css({
    px: 2,
    pb: 4,
    md: {
        px: 3
    }
});
import {useEffect, useId, useRef} from "react";
import {css} from "@panda/css";

import {Loader} from "@pg-design/loader-module";
import {ResponsiveModal} from "@pg-design/modal-module";
import {OFFER_TEST_IDS} from "@pg-mono/test-ids";

import {getUnlockChoiceOptions} from "../../../constants/unlock_choice_options";
import {IUnlockChoiceModalLayoutProps} from "../../../types/IUnlockChoiceModalLayoutProps";
import {isUnlockChoiceOfferOrReservation} from "../../../utils/is_unlock_choice_offer_or_reservation";
import {UnlockChoiceModalFooter} from "./UnlockChoiceModalFooter";
import {UnlockChoiceModalOptionCard} from "./UnlockChoiceModalOptionCard";
import {UnlockChoiceModalPriceHeader} from "./UnlockChoiceModalPriceHeader";
import {UnlockChoiceModalPriceNegotiation} from "./UnlockChoiceModalPriceNegotiation";
import {UnlockChoiceModalPriceNegotiationFooter} from "./UnlockChoiceModalPriceNegotiationFooter";
import {UnlockChoiceModalTitle} from "./UnlockChoiceModalTitle";
import {UnlockChoiceSentContent} from "./UnlockChoiceSentContent";

export const UnlockChoiceModalLayout = (props: IUnlockChoiceModalLayoutProps) => {
    const {
        isOpen,
        isLoading,
        entryPoint,
        firstName,
        price,
        offerType,
        vendorName,
        privacyPolicyUrl,
        selectedOptionId,
        isSending,
        isConfirmDisabled,
        confirmation,
        isPriceNegotiationShown,
        isPriceNegotiationArmed,
        onSelectOption,
        onConfirm,
        onClose,
        onAfterClose,
        zIndex
    } = props;

    const titleId = useId();
    const descriptionId = useId();
    const optionInputName = useId();
    const radioGroupRef = useRef<HTMLDivElement>(null);
    const wasLoadingRef = useRef(isLoading);
    const isConfirmation = confirmation !== null;
    const isPriceNegotiation = !isConfirmation && isPriceNegotiationShown;
    const isChoice = !isConfirmation && !isPriceNegotiation;
    const isDescribed = isConfirmation || isPriceNegotiation;

    useEffect(() => {
        const wasLoading = wasLoadingRef.current;
        wasLoadingRef.current = isLoading;

        const radioGroup = radioGroupRef.current;
        const dialog = radioGroup?.closest('[role="dialog"]');
        const activeElement = document.activeElement;

        if (!wasLoading || isLoading || !radioGroup || !dialog || (activeElement !== dialog && dialog.contains(activeElement))) {
            return;
        }

        radioGroup.querySelector<HTMLInputElement>("input:checked")?.focus({preventScroll: true});
    }, [isLoading]);

    useEffect(() => {
        if (!isDescribed) {
            return;
        }

        const dialog = document.getElementById(titleId)?.closest<HTMLElement>('[role="dialog"]');

        if (dialog && !dialog.contains(document.activeElement)) {
            dialog.focus({preventScroll: true});
        }
    }, [isConfirmation, isPriceNegotiation]);

    const options = getUnlockChoiceOptions(entryPoint, offerType);
    const selectedOption = options.find((option) => option.id === selectedOptionId) ?? options[0];
    const headerPrice = !isLoading && isUnlockChoiceOfferOrReservation(entryPoint) ? price : null;
    const isLabelledByTitle = isDescribed || !isLoading;

    const getDataTestId = () => {
        if (confirmation) {
            return OFFER_TEST_IDS.LEAD_FORM.UNLOCK_CHOICE_CONFIRMATION;
        }

        if (isPriceNegotiation) {
            return OFFER_TEST_IDS.LEAD_FORM.UNLOCK_CHOICE_PRICE_NEGOTIATION;
        }

        return isLoading ? OFFER_TEST_IDS.LEAD_FORM.UNLOCK_CHOICE_LOADER : OFFER_TEST_IDS.LEAD_FORM.UNLOCK_CHOICE_MODAL;
    };

    const getHeaderClassName = () => {
        if (isLoading) {
            return loaderHeaderStyle;
        }

        return headerPrice ? priceHeaderStyle : titleHeaderStyle;
    };

    const getBodyClassName = () => {
        if (isDescribed) {
            return undefined;
        }

        if (isLoading) {
            return loaderBodyStyle;
        }

        return headerPrice ? priceBodyStyle : titleBodyStyle;
    };

    const renderHeaderContent = () => {
        if (isLoading) {
            return null;
        }

        if (headerPrice) {
            return <UnlockChoiceModalPriceHeader price={headerPrice} offerType={offerType} />;
        }

        return <UnlockChoiceModalTitle id={titleId} firstName={firstName} variant="header" className={headerTitleStyle} />;
    };

    const renderBodyContent = () => {
        if (confirmation) {
            return (
                <UnlockChoiceSentContent titleId={titleId} descriptionId={descriptionId} vendorName={confirmation.vendorName} subject={confirmation.subject} />
            );
        }

        if (isPriceNegotiation) {
            return <UnlockChoiceModalPriceNegotiation titleId={titleId} descriptionId={descriptionId} offerType={offerType} />;
        }

        if (isLoading) {
            return <Loader size="lg" fullWidth />;
        }

        return (
            <>
                {headerPrice && <UnlockChoiceModalTitle id={titleId} firstName={firstName} variant="question" />}

                <div ref={radioGroupRef} role="radiogroup" aria-labelledby={titleId} className={headerPrice ? priceOptionsStyle : titleOptionsStyle}>
                    {options.map((option) => (
                        <UnlockChoiceModalOptionCard
                            key={option.id}
                            option={option}
                            inputName={optionInputName}
                            isSelected={option.id === selectedOption.id}
                            isDisabled={isSending}
                            onSelect={onSelectOption}
                        />
                    ))}
                </div>
            </>
        );
    };

    return (
        <ResponsiveModal
            isOpen={isOpen}
            onClose={onClose}
            onAfterClose={onAfterClose}
            shouldCloseOnOverlayClick={isPriceNegotiation && isPriceNegotiationArmed}
            zIndex={zIndex}
            contentLabel={isLabelledByTitle ? undefined : "Wczytywanie opcji"}
            ariaLabelledBy={isLabelledByTitle ? titleId : undefined}
            ariaDescribedBy={isDescribed ? descriptionId : undefined}
            dataTestId={getDataTestId()}
        >
            {isChoice && (
                <ResponsiveModal.Header divider={headerPrice !== null} className={getHeaderClassName()}>
                    {renderHeaderContent()}
                    <ResponsiveModal.CloseButton
                        className={headerPrice ? priceHeaderCloseButtonStyle : undefined}
                        dataTestId={OFFER_TEST_IDS.LEAD_FORM.UNLOCK_CHOICE_CLOSE_BUTTON}
                    />
                </ResponsiveModal.Header>
            )}

            <ResponsiveModal.Body className={getBodyClassName()}>{renderBodyContent()}</ResponsiveModal.Body>

            {isPriceNegotiation && (
                <UnlockChoiceModalPriceNegotiationFooter
                    vendorName={vendorName}
                    privacyPolicyUrl={privacyPolicyUrl}
                    isSending={isSending}
                    isConfirmDisabled={isConfirmDisabled}
                    isArmed={isPriceNegotiationArmed}
                    onConfirm={onConfirm}
                />
            )}

            {isChoice && !isLoading && (
                <UnlockChoiceModalFooter
                    vendorName={vendorName}
                    privacyPolicyUrl={privacyPolicyUrl}
                    isClauseVisible={selectedOption.sendsLead}
                    ctaText={selectedOption.ctaText}
                    isSending={isSending}
                    isConfirmDisabled={isConfirmDisabled}
                    onConfirm={onConfirm}
                />
            )}
        </ResponsiveModal>
    );
};

const loaderHeaderStyle = css({
    display: "flex",
    justifyContent: "flex-end",
    px: 2,
    py: 1.5,
    md: {
        px: 3
    }
});

const priceHeaderStyle = css({
    position: "relative",
    pt: 1.5,
    pb: 3,
    pl: 2,
    pr: 6,
    md: {
        pb: 1.5,
        pl: 3,
        pr: 7
    }
});

const priceHeaderCloseButtonStyle = css({
    position: "absolute",
    top: 1.5,
    right: 2,
    md: {
        right: 3
    }
});

const titleHeaderStyle = css({
    display: "flex",
    alignItems: "flex-start",
    columnGap: "1rem",
    px: 2,
    py: 1.5,
    md: {
        px: 3
    }
});

const headerTitleStyle = css({
    flex: "1 1 auto",
    minWidth: 0
});

const priceBodyStyle = css({
    px: 2,
    pt: 2,
    pb: 1,
    md: {
        px: 3,
        pt: 3.5,
        pb: 2
    }
});

const titleBodyStyle = css({
    px: 2,
    pt: 1,
    pb: 1,
    md: {
        px: 3,
        pt: 3,
        pb: 2
    }
});

const priceOptionsStyle = css({
    display: "flex",
    flexDirection: "column",
    rowGap: 1.5,
    pt: 1.5,
    md: {
        rowGap: 2,
        pt: 3.5
    }
});

const titleOptionsStyle = css({
    display: "flex",
    flexDirection: "column",
    rowGap: 1.5,
    md: {
        rowGap: 2
    }
});

const loaderBodyStyle = css({
    px: 2,
    pb: 4,
    md: {
        px: 3
    }
});
